import React, { useMemo, useState } from "react";
import styles from "./poll.module.scss";
import { KoButton } from "packages";
import KoIcon from "@kwapio/icons";
import { Progress } from "antd";
import { FeedAPI } from "./feed.service";
import { useSelector } from "react-redux";

export default function PollCard({ poll = {} }) {
  const userById = useSelector((state) => state?.userById);
  const userDetails = userById?.data || {};

  const {
    pollId,
    title,
    description,
    answerOptions = [],
    settings = {},
    votings = []
  } = poll;

  const isMultiple = settings?.isMultipleChoice;

  /* ----------------------------------------------------
     UTIL: Build vote counts from API structure
  ---------------------------------------------------- */
  const voteCounts = useMemo(() => {
    const counts = {};
    votings?.forEach((vote) => {
      (vote.answers || []).forEach((ans) => {
        counts[ans] = (counts[ans] || 0) + 1;
      });
    });
    return counts;
  }, [votings]);

  const totalVotes = votings?.length;

  const getPercentage = (optionValue) => {
    if (!totalVotes) return 0;
    const count = voteCounts[optionValue] || 0;
    return Math.round((count / totalVotes) * 100);
  };

  /* ----------------------------------------------------
     Detect if current user already voted
  ---------------------------------------------------- */
  const hasUserVoted = useMemo(() => {
    return votings?.some(
      (v) => v?.profile?.email === userDetails?.email
    );
  }, [votings, userDetails]);

  const [selected, setSelected] = useState([]);
  const [submitted, setSubmitted] = useState(hasUserVoted);

  /* ----------------------------------------------------
     Select option
  ---------------------------------------------------- */
  const handleSelect = (value) => {
    if (submitted) return;

    if (isMultiple) {
      setSelected((prev) =>
        prev.includes(value)
          ? prev.filter((v) => v !== value)
          : [...prev, value]
      );
    } else {
      setSelected([value]);
    }
  };

  /* ----------------------------------------------------
     Submit vote
  ---------------------------------------------------- */
  const handleSubmit = () => {
    if (!selected.length || submitted) return;

    setSubmitted(true);

    const payload = {
      pollId,
      answers: selected,
      userId: userDetails?.userId
    };

    FeedAPI.postPollVote(payload).catch(() => {});
  };

  /* ----------------------------------------------------
     RENDER
  ---------------------------------------------------- */
  return (
    <div className={styles.pollCard}>
      <h3 className={styles.header}>
        <KoIcon name="PollOutlined" width={18} /> Poll
      </h3>

      <div className={styles.pollTitle}>{title}</div>

      {description && (
        <div className={styles.pollDescription}>{description}</div>
      )}

      <div className={styles.pollOptions}>
        {answerOptions?.map((option) => {
          const percent = getPercentage(option.value);
          const count = voteCounts[option.value] || 0;
          const isSelected = selected.includes(option.value);

          return (
            <div
              key={option.value}
              className={`${styles.option} ${
                isSelected ? styles.selected : ""
              }`}
              onClick={() => handleSelect(option.value)}
            >
              <div className={styles.optionHeader}>
                <span>{option.label}</span>
                {submitted && (
                  <span className={styles.voteCount}>
                    {count} vote{count !== 1 ? "s" : ""}
                  </span>
                )}
              </div>

              {submitted && (
                <Progress
                  percent={percent}
                  percentPosition={{ align: "end", type: "inner" }}
                  size={[320, 18]}
                  strokeColor="#10595c"
                  showInfo={false}
                />
              )}
            </div>
          );
        })}
      </div>

      {!submitted && (
        <KoButton
          type="primary"
          label="Vote"
          disabled={!selected.length}
          onClick={handleSubmit}
          className={styles.voteBtn}
        />
      )}

      <div className={styles.pollFooter}>
        <span>{totalVotes} votes</span>
      </div>
    </div>
  );
}
